import React, { FunctionComponent, useCallback } from 'react';
import { merge } from 'ramda';
import Capitalize from 'capitalize';
import { format } from 'd3';

import DataMap from 'datamaps/dist/datamaps.usa';

import { map } from 'helpers/safeRamda';
import { theme } from 'config';
import { Bubble, MetricsByMetro } from 'components/TopMetros/TopMetros.helpers';

type Props = {
  metricsData: MetricsByMetro[];
  height: number;
};

const MIN_RADIUS = 4;
const MAX_RADIUS = 10;
const BUBBLE_TRANSACTION_LIMIT = 1000;
const DEFAULT_COORDS = {
  long: -98.58333,
  lat: 39.83333,
};

const customDataMapConfig = {
  geographyConfig: {
    highlightBorderColor: theme.seafoamGreen,
    highlightFillColor: '#42337f',
    borderColor: '#42337f',
  },
  fills: {
    POINT: theme.seafoamGreen,
    defaultFill: '#332372', // Any hex, color name or rgb/rgba value
  },
  bubblesConfig: {
    borderWidth: 0,
    highlightBorderColor: theme.seafoamGreen,
    highlightFillColor: '#fff',
  },
};

const calculateRadius = (transactions: number) => {
  const radius = Math.round(
    (transactions / BUBBLE_TRANSACTION_LIMIT) * MAX_RADIUS,
  );

  if (radius < MIN_RADIUS) {
    return MIN_RADIUS;
  }

  if (radius > MAX_RADIUS) {
    return MAX_RADIUS;
  }

  return radius;
};

const getBubbles = map(
  ({
    city,
    latitude,
    longitude,
    numberOfTransactions,
    avgAov,
  }: MetricsByMetro): Bubble | undefined => {
    const radius = calculateRadius(numberOfTransactions);
    const { lat, long } = DEFAULT_COORDS;

    const bubble: Bubble = {
      radius,
      longitude: longitude === 0 ? long : longitude,
      latitude: latitude === 0 ? lat : latitude,
      fillKey: 'POINT',
      country: 'USA',
      name: `
        City: ${Capitalize.words(city)},
        Aov: ${format('$.2f')(avgAov)},
        Transactions: ${numberOfTransactions}
      `,
    };

    return bubble;
  },
);

const renderMap = (
  transactionMetricsByMetro: MetricsByMetro[],
  height: number,
  node: Node,
) => {
  const bubblesData = getBubbles(transactionMetricsByMetro) as Bubble[];
  if (node) {
    const dataMap = new DataMap(
      merge(
        {
          height,
          element: node,
          scope: 'usa',
          responsive: false,
        },
        customDataMapConfig,
      ),
    );

    return dataMap.bubbles(bubblesData, { animate: false });
  }

  return null;
};

const TopMetrosMap: FunctionComponent<Props> = ({ metricsData, height }) => {
  const drawMapRef = useCallback((node) => {
    if (node !== null) {
      return renderMap(metricsData as MetricsByMetro[], height, node);
    }
  }, []);

  return <div id="mapRef" ref={drawMapRef} />;
};

export default TopMetrosMap;
